import AbstractBetslipService from './abstract-betslip-service.js';
import MojitoCore from 'mojito/core';
import { omit, without } from 'mojito/utils';
import ApiRequestProvider from 'services/common/api-request/request-provider.js';

const BASE_URL = '/betslip';
const logger = MojitoCore.logger.get('BetslipService');
const NamedStorageService = MojitoCore.Services.Storage.NamedService;

const ADD_PART_URL = 'part/add';
const UPDATE_PART_URL = 'part/update';
const REMOVE_PART_URL = 'part/remove';
const SWITCH_PART_SELECTION_URL = 'part/switch-selection';

const ADD_COMPOSITE_URL = 'composite/add';
const UPDATE_COMPOSITE_URL = 'composite/update';
const REMOVE_COMPOSITE_URL = 'composite/remove';
const ADD_ODDS_BOOST_TOKEN = 'add-odds-boost_token';
const REMOVE_ODDS_BOOST_TOKEN = 'remove-odds-boost_token';
const UPDATE_SAVED_BET = 'update-saved-bet';
const GET_SHARE_URL = 'get-share-url';

const betslipStorage = new NamedStorageService('betslipData');
const savedBetsStorage = new NamedStorageService('savedBetIds');

/**
 * Betslip service config.
 *
 * @typedef BetslipServiceConfig
 * @type {object}
 * @property {string} serviceUrl - Betslip API endpoint URL.
 *
 * @memberof Mojito.Services.Betslip
 */

/**
 * Class offering the possibility to interact with the mojito betslip API.
 *
 * @class BetslipService
 * @name service
 * @extends Mojito.Services.Betslip.AbstractBetslipService
 * @memberof Mojito.Services.Betslip
 */
export class BetslipService extends AbstractBetslipService {
    constructor() {
        super();
        this.addFreeBet = this.freeBetCallFactory('add-free-bet', 'add free bet');
        this.removeFreeBet = this.freeBetCallFactory('remove-free-bet', 'remove free bet');
    }
    /**
     * Configure service.
     *
     * @param {Mojito.Services.Betslip.BetslipServiceConfig} config - Service configuration object.
     *
     * @function Mojito.Services.Betslip.service#configure
     */
    configure(config) {
        this.serviceUrl = config.serviceUrl;
        this.requestFactory = new ApiRequestProvider(logger);
        this.apiUrl = `${this.serviceUrl + BASE_URL}/`;
    }

    /**
     * Performs init betslip call with provided betslip state.
     *
     * @param {Mojito.Services.UserSettings.types.ODDS_FORMAT} oddsFormat - Odds format.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on init call response.
     * @function Mojito.Services.Betslip.service#initBetslip
     */
    initBetslip(oddsFormat, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(`${this.apiUrl}init`)
            .withDescription('init betslip')
            .withCredentials()
            .send({ oddsFormat, state });
    }

    /**
     * Generate api endpoint url for specified api call.
     *
     * @param {string} method - Api call (method).
     *
     * @returns {string} API call url.
     * @function Mojito.Services.Betslip.service#getEndpointUrlFor
     */
    getEndpointUrlFor(method) {
        return `${this.apiUrl}${method}`;
    }

    /**
     * Add part to the betslip.
     *
     * @param {Mojito.Services.Betslip.types.AddSelectionPartPayload} payload - Add part payload data.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on add part call response.
     * @function Mojito.Services.Betslip.service#addPart
     */
    addPart(payload, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }

        const data = {
            ...payload,
            state,
        };

        if (payload.parent) {
            data.parent = omit(payload.parent, 'marketId');
        }

        return this.requestFactory
            .post(this.getEndpointUrlFor(ADD_PART_URL))
            .withDescription('add part')
            .withCredentials()
            .send(data);
    }

    /**
     * Remove betslip part.
     *
     * @param {string} selectionId - Selection id.
     * @param {string} betId - Bet id.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on remove part call response.
     * @function Mojito.Services.Betslip.service#removePart
     */
    removePart(selectionId, betId, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(this.getEndpointUrlFor(REMOVE_PART_URL))
            .withDescription('remove part')
            .withCredentials()
            .send({ selectionId, betId, state });
    }

    /**
     * Update selections within betslip part.
     *
     * @param {Array<Mojito.Services.Betslip.types.Selection>} selections - Selections to update in betslip.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on update part call response.
     * @function Mojito.Services.Betslip.service#updatePart
     */
    updatePart(selections, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(this.getEndpointUrlFor(UPDATE_PART_URL))
            .withDescription('update part')
            .withCredentials()
            .send({ selections, state });
    }

    switchPartSelection(selectionId, betRef, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(this.getEndpointUrlFor(SWITCH_PART_SELECTION_URL))
            .withDescription('switch part selection')
            .withCredentials()
            .send({ selectionId, betRef, state });
    }

    /**
     * Add composite to the betslip.
     *
     * @param {Mojito.Services.Betslip.types.AddCompositePayload} payload - Add betslip composite payload.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on add composite call response.
     *
     * @function Mojito.Services.Betslip.service#addComposite
     */
    addComposite(payload, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(this.getEndpointUrlFor(ADD_COMPOSITE_URL))
            .withDescription('add composite')
            .withCredentials()
            .send({ ...payload, state });
    }

    /**
     * Update composite in the betslip.
     *
     * @param {Mojito.Services.Betslip.types.UpdateCompositePayload} payload - Update betslip composite payload.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on update composite call response.
     *
     * @function Mojito.Services.Betslip.service#updateComposite
     */
    updateComposite(payload, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(this.getEndpointUrlFor(UPDATE_COMPOSITE_URL))
            .withDescription('update composite')
            .withCredentials()
            .send({ ...payload, state });
    }

    /**
     * Remove composite from the betslip.
     *
     * @param {Array} selectionIds - List of selection ids within composite.
     * @param {string} betId - Bet id.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on remove composite call response.
     *
     * @function Mojito.Services.Betslip.service#removeComposite
     */
    removeComposite(selectionIds, betId, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(this.getEndpointUrlFor(REMOVE_COMPOSITE_URL))
            .withDescription('remove composite')
            .withCredentials()
            .send({ selectionIds, betId, state });
    }

    /**
     * Set the stake for a specific bet in a betslip.
     *
     * @param {Mojito.Services.Betslip.types.SetStakeRequest} payload - Set stake payload data.
     * @param {*} state - Betslip state.
     * @param {AbortSignal} [abortSignal] - Abort signal instance to allow pending request cancellation.
     *
     * @returns {Promise} Promise resolves on set stake call response.
     * @function Mojito.Services.Betslip.service#setStake
     */
    setStake(payload, state, abortSignal) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        const request = this.requestFactory
            .post(`${this.apiUrl}stake`)
            .withDescription('set stake')
            .withCredentials();

        abortSignal?.addEventListener('abort', () => request.abort());

        return request.send({ ...payload, state });
    }

    /**
     * Activate a stake group.
     *
     * @param {Mojito.Services.Betslip.types.STAKE_GROUP_NAME} stakeGroupName - Stake group name.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on activate a stake group call response.
     * @function Mojito.Services.Betslip.service#activateStakeGroup
     */
    activateStakeGroup(stakeGroupName, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }

        return this.requestFactory
            .post(`${this.apiUrl}stake/activate-group`)
            .withDescription('activate stake group')
            .withCredentials()
            .send({
                stakeGroupName,
                state,
            });
    }

    activateTeaserType(type, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }

        return this.requestFactory
            .post(`${this.apiUrl}teaser/activate-type`)
            .withDescription('activate teaser type')
            .withCredentials()
            .send({ type, state });
    }

    /**
     * Remove a stake group.
     *
     * @param {Mojito.Services.Betslip.types.STAKE_GROUP_NAME} stakeGroupName - Stake group name.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on remove a stake group call response.
     * @function Mojito.Services.Betslip.service#removeStakeGroup
     */
    removeStakeGroup(stakeGroupName, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }

        return this.requestFactory
            .post(`${this.apiUrl}stake/clear-group`)
            .withDescription('remove stake group')
            .withCredentials()
            .send({
                stakeGroupName,
                state,
            });
    }

    /**
     * Execute actions.
     *
     * @param {Array<Mojito.Services.Betslip.types.Action>} actions - List of actions.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on execute actions call response.
     * @function Mojito.Services.Betslip.service#executeActions
     */
    executeActions(actions, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(`${this.apiUrl}execute-actions`)
            .withDescription('execute actions')
            .withCredentials()
            .send({
                actions,
                state,
            });
    }

    /**
     * Place a betslip.
     *
     * @param {Mojito.Services.Betslip.types.STAKE_GROUP_NAME} stakeGroupName - Stake group name.
     * @param {Array<Mojito.Services.Betslip.types.PRICE_CHANGE_POLICY>} priceChangePolicies - Price change policies list.
     * @param {string} currency - Currency.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on place betslip call response.
     * @function Mojito.Services.Betslip.service#placeBetslip
     */
    placeBetslip(stakeGroupName, priceChangePolicies, currency, state) {
        return this.requestFactory
            .post(`${this.apiUrl}place`)
            .withDescription('place betslip')
            .withCredentials()
            .send({
                stakeGroupName,
                currency,
                priceChangePolicies,
                state,
            });
    }

    /**
     * Save a betslip.
     *
     * @param {Mojito.Services.Betslip.types.STAKE_GROUP_NAME} stakeGroupName - Stake group name.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on save betslip call response.
     * @function Mojito.Services.Betslip.service#saveBetslip
     */
    saveBetslip(stakeGroupName, state) {
        return this.requestFactory
            .post(`${this.apiUrl}save`)
            .withDescription('save betslip')
            .withCredentials()
            .send({ stakeGroupName, state });
    }

    /**
     * Clear betslip.
     *
     * @param {Array<Mojito.Services.Betslip.types.BETSLIP_PART>} retainParts - Retain parts.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on clear betslip call response.
     * @function Mojito.Services.Betslip.service#clearBetslip
     */
    clearBetslip(retainParts, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(`${this.apiUrl}clear`)
            .withDescription('clear betslip')
            .withCredentials()
            .send({
                retainParts,
                state,
            });
    }

    /**
     * Validate betslip bonuses.
     * Typically used to validate acc bonuses on current betslip state.
     * Note: this call is only valuable with valid user session.
     *
     * @param {*} state - Betslip state.
     * @param {AbortSignal} [abortSignal] - Abort signal instance to allow pending request cancellation.
     *
     * @returns {Promise} Promise resolves on validate betslip bonuses call response.
     * @function Mojito.Services.Betslip.service#validateBonuses
     */
    validateBonuses(state, abortSignal) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        const request = this.requestFactory
            .post(`${this.apiUrl}bonus/validate`)
            .withDescription('validate bonuses')
            .withCredentials();

        abortSignal?.addEventListener('abort', () => request.abort());

        return request.send({ state });
    }

    /**
     * Refresh betslip bonuses.
     * Typically, forces backend to reset user bonus offers on betslip state.
     * Note: this call is only valuable with valid user session.
     *
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on validate betslip bonuses call response.
     * @function Mojito.Services.Betslip.service#refreshBonusOffers
     */
    refreshBonusOffers(state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(`${this.apiUrl}bonus/refresh-offers`)
            .withDescription('refresh bonus offers')
            .withCredentials()
            .send({ state });
    }

    /**
     * Factory function used to build concrete free bet request method.
     *
     * @private
     *
     * @param {string} call - Name of the free bet endpoint.
     * @param {string} description - Call description.
     *
     * @returns {Function} Function that will perform request to provided free bet endpoint.
     * @function Mojito.Services.Betslip.service#freeBetCallFactory
     */
    freeBetCallFactory(call, description) {
        return (token, state) => {
            if (!this.validateServiceUrl()) {
                return Promise.reject();
            }
            return this.requestFactory
                .post(`${this.apiUrl}bonus/${call}`)
                .withDescription(description)
                .withCredentials()
                .send({ token, state });
        };
    }

    /**
     * Add odds boost token.
     *
     * @param {Mojito.Services.Betslip.types.OddsBoostTokenRequest} payload - Add odds boost token payload data.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on add odds boost token call response.
     * @function Mojito.Services.Betslip.service#addOddsBoostToken
     */
    addOddsBoostToken(payload, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }

        return this.requestFactory
            .post(this.getEndpointUrlFor(`bonus/${ADD_ODDS_BOOST_TOKEN}`))
            .withDescription('add odds boost token')
            .withCredentials()
            .send({ ...payload, state });
    }

    /**
     * Remove odds boost token.
     *
     * @param {Mojito.Services.Betslip.types.OddsBoostTokenRequest} payload - Remove odds boost token payload data.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Promise resolves on remove odds boost token call response.
     * @function Mojito.Services.Betslip.service#removeOddsBoostToken
     */
    removeOddsBoostToken(payload, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }

        return this.requestFactory
            .post(this.getEndpointUrlFor(`bonus/${REMOVE_ODDS_BOOST_TOKEN}`))
            .withDescription('remove odds boost token')
            .withCredentials()
            .send({ ...payload, state });
    }

    /**
     * Get overask status.
     *
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Overask status promise.
     * @function Mojito.Services.Betslip.service#getOveraskStatus
     */
    getOveraskStatus(state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(`${this.apiUrl}overask/status`)
            .withDescription('get overask status')
            .withCredentials()
            .send({ state });
    }

    /**
     * Update overask.
     *
     * @param {Mojito.Services.Betslip.types.OveraskUpdatePayload} payload - Payload.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Overask update promise.
     * @function Mojito.Services.Betslip.service#updateOverask
     */
    updateOverask(payload, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(`${this.apiUrl}overask/update`)
            .withDescription('overask update')
            .withCredentials()
            .send({ ...payload, state });
    }

    /**
     * Toggle banker for bet.
     *
     * @param {string} betId - Bet ID.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Toggle banker promise.
     * @function Mojito.Services.Betslip.service#toggleBanker
     */
    toggleBanker(betId, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(`${this.apiUrl}bankers/toggle`)
            .withDescription('toggle banker for bet')
            .withCredentials()
            .send({ betId, state });
    }

    /**
     * Update saved bet with new info from selections list. Note: for Quantum it will not store the bet in saved bets service.
     * It will just return new bet object with new calculations to present on a view.
     *
     * @param {Array<Mojito.Services.Betslip.types.Selection>} selections - Selections to update in betslip.
     * @param {string} betId - Bet id.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Update saved bet promise. Will contain betslip object or error in the payload.
     *
     * @function Mojito.Services.Betslip.service#updateSavedBet
     */
    // eslint-disable-next-line no-unused-vars
    updateSavedBet(selections, betId, state) {
        return Promise.reject({});
        // TODO: Uncomment in MOJ-15820. Updated saved bet endpoint is not implemented yet on betslip API.
        /* if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(this.getEndpointUrlFor(UPDATE_SAVED_BET))
            .withDescription('update saved bet')
            .withCredentials()
            .send({ selections, betId, save: true, state }); */
    }

    /**
     * Copy saved bet. Will end up with a new betslip containing selections from the saved bet.
     *
     * @param {string} betId - Bet id.
     * @param {*} state - Betslip state.
     *
     * @returns {Promise} Update saved bet promise. Will contain betslip object or error in the payload.
     *
     * @function Mojito.Services.Betslip.service#copySavedBet
     */
    copySavedBet(betId, state) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(this.getEndpointUrlFor(UPDATE_SAVED_BET))
            .withDescription('copy saved bet')
            .withCredentials()
            .send({ betId, state });
    }

    /**
     * Stores betslip data in a local storage in 'betslipData' property under dataType key.
     *
     * @param {object} data - Betslip data.
     * @param {Mojito.Services.Betslip.types.BETSLIP_STORAGE_TYPE} dataType - The type of the data, will be used as a key.
     *
     * @function Mojito.Services.Betslip.service#storeBetslipData
     */
    storeBetslipData(data, dataType) {
        const currentItem = betslipStorage.getItem() || {};
        const currentData = currentItem[dataType] || {};
        betslipStorage.setItem({ ...currentItem, [dataType]: { ...currentData, ...data } });
    }

    /**
     * Stores saved bet ids for later bet placement.
     *
     * @param {Array<string>} betIds - Bet ids list to be stored.
     *
     * @function Mojito.Services.Betslip.service#storeSavedBetIds
     */
    storeSavedBetIds(betIds) {
        if (betIds) {
            const currentBetIds = savedBetsStorage.getItem() || [];
            savedBetsStorage.setItem([...currentBetIds, ...betIds]);
        }
    }

    /**
     * Removes betslip data from local storage.
     *
     * @param {Mojito.Services.Betslip.types.BETSLIP_STORAGE_TYPE} dataType - The type of the data to be removed.
     *
     * @function Mojito.Services.Betslip.service#cleanBetslipData
     */
    cleanBetslipData(dataType) {
        const currentItem = betslipStorage.getItem();
        if (currentItem) {
            delete currentItem[dataType];
            betslipStorage.setItem({ ...currentItem });
        }
    }

    /**
     * Fetch betslip data from a local storage.
     *
     * @param {Mojito.Services.Betslip.types.BETSLIP_STORAGE_TYPE} [dataType] - The type of the data to be fetched.
     *
     * @returns {object} Betslip data from local storage.
     * @function Mojito.Services.Betslip.service#fetchBetslipData
     */
    fetchBetslipData(dataType) {
        const item = betslipStorage.getItem() || {};
        return dataType ? item[dataType] : item;
    }

    /**
     * Fetch stored bet ids from a local storage.
     *
     * @returns {Promise} Betslip data from local storage.
     * @function Mojito.Services.Betslip.service#fetchSavedBetIds
     */
    fetchSavedBetIds() {
        const betIds = savedBetsStorage.getItem() || [];
        return Promise.resolve(betIds);
    }

    /**
     * Removes saved bet ids from local storage.
     *
     * @param {Array<string>} ids - The ids of the bets.
     *
     * @function Mojito.Services.Betslip.dataRetriever#removeSavedBetId
     */
    removeSavedBetIds(ids) {
        const existingBetIds = savedBetsStorage.getItem() || [];
        const betIds = without(existingBetIds, ...ids);
        savedBetsStorage.setItem(betIds);
    }

    validateServiceUrl() {
        if (!this.serviceUrl) {
            logger.warn('serviceUrl is missing.');
            return false;
        }
        return true;
    }

    /**
     * Get shared url.
     *
     * @param {string} path - Absolute path.
     * @param {Array<string>} betRefs - The list of betRefs.
     * @param {string} affiliate - Affiliate id.
     *
     * @returns {Promise} Resolves with payload object `{url: 'https://bit.ly/QfdsfeerqwQ%22%7D'}` or error in the payload.
     * @function Mojito.Services.Betslip.service#getShareUrl
     */
    getShareUrl(path, betRefs, affiliate) {
        return this.requestFactory
            .post(this.getEndpointUrlFor(GET_SHARE_URL))
            .withDescription('get share url')
            .withCredentials()
            .send({
                path,
                betRefs,
                ...(affiliate && { affiliate }),
            });
    }
}

export default new BetslipService();
